import { useWsEvents } from '../hooks/useWsEvents';
import { ScoreBadge, PlayerInfoBlock } from '../components/PlayerWidgetParts';
import type { PlayerWidgetData, PlayerPhotoData } from '../types/widgets';
import { fonts, glass, glassBorder, glassText, brandGradient } from '../styles/overlayTokens';

// ─── Constants ────────────────────────────────────────────────────────────────

const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;
const PANEL_WIDTH_PRESETS: Record<'compact' | 'medium' | 'full', number> = {
  compact: 330,
  medium: 360,
  full: 400,
};
const DEFAULT_PANEL_WIDTH = PANEL_WIDTH_PRESETS.compact;

const INITIAL_PLAYER: PlayerWidgetData = {
  p1: { name: 'Player 1', archetype: '' },
  p2: { name: 'Player 2', archetype: '' },
  p1Score: 0,
  p2Score: 0,
  bestOf: 3,
  turnIndicator: null,
  p1Identity: null,
  p2Identity: null,
};

const INITIAL_PHOTO: PlayerPhotoData = {};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true when there is meaningful player data beyond the default name.
 * Used to determine whether to render health / record rows.
 */
function hasExtendedData(data: PlayerWidgetData, player: 'p1' | 'p2'): boolean {
  const health = player === 'p1' ? data.p1Health : data.p2Health;
  const healths = player === 'p1' ? data.p1Healths : data.p2Healths;
  const record = player === 'p1' ? data.p1Record : data.p2Record;
  return health != null || (healths != null && healths.length > 0) || record != null;
}

// ─── Side Panel ───────────────────────────────────────────────────────────────

interface SidePanelProps {
  /** Which side this panel is anchored to. */
  side: 'left' | 'right';
  name: string;
  archetype?: string;
  record?: string;
  photoUrl?: string | null;
  showPhoto: boolean;
  glowActive: boolean;
  health?: number;
  healthStage: 1 | 2;
  healthConfig: PlayerWidgetData['healthConfig'];
  showHealth: boolean;
  /** True when this player is the active/leading player — renders accent bar. */
  isActive: boolean;
  /** True when there is extended data (health / record) to show below the name. */
  showExtendedRows: boolean;
  avatarShape?: 'circle' | 'square';
  score: number;
  opponentScore: number;
  showScores: boolean;
  sidePlayerAlign?: 'top' | 'center' | 'bottom';
  panelWidth: number;
  /** Array-based health configs for multi-bar games. */
  healthConfigs?: PlayerWidgetData['healthConfigs'];
  /** Per-config health values, parallel to healthConfigs. */
  healths?: number[] | null;
  /** Per-config health stages, parallel to healthConfigs. */
  healthStages?: (1 | 2)[] | null;
  /** Chrono Core pilot card thumbnail URL. */
  pilotCardUrl?: string | null;
  /** Chrono Core core card thumbnail URL. */
  coreCardUrl?: string | null;
  /** Per-player effective max values for each health config (overrides healthConfigs[i].maxValue). */
  healthMaxes?: number[] | null;
  /** Explicit circle diameter (px) for the pilot/core stacked avatars. Map: compact→200, medium→224, full→248. */
  avatarCircleDiameter: number;
  /** Photo avatar size (px) for PlayerAvatar (non-ChronoCore games). Map: compact→240, medium→272, full→304. */
  photoAvatarSize: number;
}

function SidePanel({
  side,
  name,
  archetype,
  record,
  photoUrl,
  showPhoto,
  glowActive,
  health,
  healthStage,
  healthConfig,
  showHealth,
  isActive,
  showExtendedRows,
  avatarShape,
  score,
  opponentScore,
  showScores,
  sidePlayerAlign = 'center',
  panelWidth,
  healthConfigs,
  healths,
  healthStages,
  pilotCardUrl,
  coreCardUrl,
  healthMaxes,
  avatarCircleDiameter,
  photoAvatarSize,
}: SidePanelProps) {
  const isLeft = side === 'left';
  // Right panel is always P2 — mirror the avatar stack so pilot sits top-right, core bottom-left.
  const mirrorAvatarStack = !isLeft;

  const justifyMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' } as const;
  const justifyContent = justifyMap[sidePlayerAlign];

  const panelBg = { background: glass.surfaceHeavy };

  const borderStyle = isActive
    ? { border: '1px solid transparent' }
    : { border: `1px solid ${glassBorder.light}` };

  return (
    <div
      className={isLeft ? 'anim-slide-from-left-edge' : 'anim-slide-from-right-edge'}
      style={{
        position: 'absolute',
        top: 0,
        [isLeft ? 'left' : 'right']: 0,
        width: panelWidth,
        height: CANVAS_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent,
        padding: '32px 20px',
        gap: 16,
        overflow: 'hidden',
        boxSizing: 'border-box',
        // Box-shadow for depth
        boxShadow: isLeft
          ? '4px 0 32px rgba(0,0,0,0.5)'
          : '-4px 0 32px rgba(0,0,0,0.5)',
        ...panelBg,
        ...borderStyle,
      }}
    >
      {/* Inner-edge accent bar — active player only */}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            [isLeft ? 'right' : 'left']: 0,
            width: 3,
            background: brandGradient.horizontal,
          }}
        />
      )}

      {/*
        PlayerInfoBlock renders avatar + name + health + record.
        align follows the panel side: left panel → 'left', right panel → 'right'.
        PlayerInfoBlock handles all internal text/row alignment from the align prop.
      */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
      }}>
        <PlayerInfoBlock
          name={name}
          archetype={archetype}
          record={showExtendedRows ? record : undefined}
          photoUrl={photoUrl}
          avatarSize={photoAvatarSize}
          showPhoto={showPhoto}
          glowActive={glowActive}
          health={showExtendedRows ? health : undefined}
          healthStage={healthStage}
          healthConfig={showExtendedRows ? healthConfig : null}
          showHealth={showExtendedRows ? showHealth : false}
          avatarShape={avatarShape}
          align={isLeft ? 'left' : 'right'}
          healthConfigs={showExtendedRows ? healthConfigs : null}
          healths={showExtendedRows ? healths : null}
          healthStages={showExtendedRows ? healthStages : null}
          pilotCardUrl={pilotCardUrl}
          coreCardUrl={coreCardUrl}
          thumbSize="large"
          avatarCircleDiameter={avatarCircleDiameter}
          healthMaxes={healthMaxes}
          mirrorAvatarStack={mirrorAvatarStack}
        />
      </div>
      {showScores && showExtendedRows && (
        <ScoreBadge score={score} isLeading={score > opponentScore} />
      )}
    </div>
  );
}

// ─── SidePlayersWidget ────────────────────────────────────────────────────────

/**
 * Full 1920x1080 transparent-center canvas overlay.
 *
 * - Left panel: Player 1 info, anchored to left edge
 * - Center: Transparent — camera/game capture shows through
 * - Right panel: Player 2 info, anchored to right edge
 * Query params:
 *   ?panelSize=compact — 330px panels, 290px usable, 200px avatar circles (default)
 *   ?panelSize=medium  — 360px panels, 320px usable, 224px avatar circles
 *   ?panelSize=full    — 400px panels, 360px usable, 248px avatar circles
 *   ?fallback=true     — dark background instead of transparent (for non-chroma browser sources)
 * Horizontal padding is 20px per side (40px total); usable width = panel width − 40px.
 */
export function SidePlayersWidget() {
  const params = new URLSearchParams(window.location.search);
  const useFallback = params.get('fallback') === 'true';
  const urlPanelSize = params.get('panelSize');

  const { data } = useWsEvents<PlayerWidgetData>('playerWidget', INITIAL_PLAYER);
  const { data: photoData } = useWsEvents<PlayerPhotoData>('playerPhoto', INITIAL_PHOTO);

  // WS data takes priority over URL param; both fall back to 'compact'
  const resolvedPanelSize = data.sidePlayerPanelSize ?? urlPanelSize ?? 'compact';
  const panelWidth = PANEL_WIDTH_PRESETS[resolvedPanelSize as keyof typeof PANEL_WIDTH_PRESETS] ?? DEFAULT_PANEL_WIDTH;

  const showPhotos = data.showPhotos ?? true;
  const showHealth = data.showHealth ?? true;
  const showScores = data.showScores ?? true;
  const avatarShape = data.avatarShape;
  const sidePlayerAlign = data.sidePlayerAlign ?? 'center';

  const p1Active = data.turnIndicator === 'p1';
  const p2Active = data.turnIndicator === 'p2';

  const p1HasExtended = hasExtendedData(data, 'p1');
  const p2HasExtended = hasExtendedData(data, 'p2');

  // Map resolved panel mode to explicit pilot/core avatar circle diameter (px).
  // Horizontal padding = 20px per side (40px total); usable width = panel width − 40px.
  //   compact → 200px circles, 1.42× footprint = 284px, 290px usable − 284px =  6px margin
  //   medium  → 216px circles, 1.42× footprint = 307px, 320px usable − 307px = 13px margin
  //   full    → 248px circles, 1.42× footprint = 352px, 360px usable − 352px =  8px margin
  const AVATAR_CIRCLE_BY_MODE = { compact: 200, medium: 216, full: 248 } as const;
  const avatarCircleDiameter = AVATAR_CIRCLE_BY_MODE[resolvedPanelSize as keyof typeof AVATAR_CIRCLE_BY_MODE] ?? 200;

  // Map resolved panel mode to photo avatar size (px) for non-ChronoCore games (PlayerAvatar single circle).
  // No footprint factor — single circle, fits directly in usable width with comfortable margin.
  //   compact → 240px (290px usable − 240px = 50px margin)
  //   medium  → 272px (320px usable − 272px = 48px margin)
  //   full    → 304px (360px usable − 304px = 56px margin)
  const PHOTO_AVATAR_BY_MODE = { compact: 240, medium: 272, full: 304 } as const;
  const photoAvatarSize = PHOTO_AVATAR_BY_MODE[resolvedPanelSize as keyof typeof PHOTO_AVATAR_BY_MODE] ?? 240;

  // The canvas root is always 1920x1080; background only set in fallback mode
  const canvasStyle: React.CSSProperties = {
    position: 'relative',
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
    fontFamily: fonts.body,
    color: glassText.primary,
    background: useFallback ? 'rgba(18,18,22,0.85)' : 'transparent',
    overflow: 'hidden',
  };

  return (
    <div style={canvasStyle}>
      {/* Left panel — Player 1 */}
      <SidePanel
        side="left"
        name={data.p1.name}
        archetype={data.p1.archetype}
        record={data.p1Record}
        photoUrl={photoData.p1Photo}
        showPhoto={showPhotos}
        glowActive={data.turnIndicator === 'p1'}
        health={data.p1Health}
        healthStage={(data.p1HealthStage ?? 1) as 1 | 2}
        healthConfig={data.healthConfig}
        showHealth={showHealth}
        isActive={p1Active}
        showExtendedRows={p1HasExtended}
        avatarShape={avatarShape}
        score={data.p1Score}
        opponentScore={data.p2Score}
        showScores={showScores}
        sidePlayerAlign={sidePlayerAlign}
        panelWidth={panelWidth}
        healthConfigs={data.healthConfigs}
        healths={data.p1Healths}
        healthStages={data.p1HealthStages}
        pilotCardUrl={data.p1.pilotCardUrl}
        coreCardUrl={data.p1.coreCardUrl}
        healthMaxes={data.p1HealthMaxes}
        avatarCircleDiameter={avatarCircleDiameter}
        photoAvatarSize={photoAvatarSize}
      />

      {/* Right panel — Player 2 */}
      <SidePanel
        side="right"
        name={data.p2.name}
        archetype={data.p2.archetype}
        record={data.p2Record}
        photoUrl={photoData.p2Photo}
        showPhoto={showPhotos}
        glowActive={data.turnIndicator === 'p2'}
        health={data.p2Health}
        healthStage={(data.p2HealthStage ?? 1) as 1 | 2}
        healthConfig={data.healthConfig}
        showHealth={showHealth}
        isActive={p2Active}
        showExtendedRows={p2HasExtended}
        avatarShape={avatarShape}
        score={data.p2Score}
        opponentScore={data.p1Score}
        showScores={showScores}
        sidePlayerAlign={sidePlayerAlign}
        panelWidth={panelWidth}
        healthConfigs={data.healthConfigs}
        healths={data.p2Healths}
        healthStages={data.p2HealthStages}
        pilotCardUrl={data.p2.pilotCardUrl}
        coreCardUrl={data.p2.coreCardUrl}
        healthMaxes={data.p2HealthMaxes}
        avatarCircleDiameter={avatarCircleDiameter}
        photoAvatarSize={photoAvatarSize}
      />

{/* Score bridge removed — scores now displayed per-panel via ScoreBadge */}
    </div>
  );
}
